import { normalizeCursorDateTime } from '../pagination/cursorDateTime';
import { appendUniqueComments } from '../social-post/socialPostDetailModel';

/** 既有留言頁大小是 20；最多再追 5 頁，避免無限 load-more。 */
export const MAX_TARGET_SEARCH_PAGES = 5;
export const COMMENT_CONTEXT_PAGE_LIMIT = 20;

const POSITIVE_ID = /^[1-9]\d*$/;

export function isExactPositiveId(value) {
  return typeof value === 'string' && POSITIVE_ID.test(value);
}

export function commentDomId(commentId) {
  return `comment-${commentId}`;
}

export function parseCommentContextQuery(search) {
  const raw = String(search || '');
  const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  const commentId = params.get('commentId');
  const replyId = params.get('replyId');
  if (commentId == null && replyId == null) {
    return { state: 'NO_CONTEXT', commentId: null, replyId: null };
  }
  if (!isExactPositiveId(commentId)) {
    return { state: 'INVALID_CONTEXT', commentId: null, replyId: null };
  }
  if (replyId != null && replyId !== '' && !isExactPositiveId(replyId)) {
    return { state: 'INVALID_CONTEXT', commentId: null, replyId: null };
  }
  return {
    state: 'READY',
    commentId,
    replyId: isExactPositiveId(replyId) ? replyId : null,
  };
}

export function normalizedPageCursor(cursor) {
  const createdAt = normalizeCursorDateTime(cursor?.cursorCreatedAt);
  const id = String(cursor?.cursorCommentId ?? '');
  if (!createdAt || !isExactPositiveId(id)) return null;
  return { cursorCreatedAt: createdAt, cursorCommentId: id };
}

export function cursorKey(cursor) {
  const normalized = normalizedPageCursor(cursor);
  if (!normalized) return null;
  return `${normalized.cursorCreatedAt}|${normalized.cursorCommentId}`;
}

function findById(items, targetId) {
  return (Array.isArray(items) ? items : []).find((item) => String(item.commentId) === targetId) || null;
}

/**
 * 在已載入列與後續分頁中尋找目標。第一頁算 1 次。
 * loadPage(cursor) 必須回傳既有 list 契約：{ items, hasMore, cursor }。
 */
export async function searchTargetAcrossPages({
  targetId,
  initialItems,
  initialCursor,
  initialHasMore,
  loadPage,
  maxPages = MAX_TARGET_SEARCH_PAGES,
}) {
  let items = Array.isArray(initialItems) ? [...initialItems] : [];
  let cursor = normalizedPageCursor(initialCursor);
  let hasMore = Boolean(initialHasMore) && cursor != null;
  const seen = new Set();
  const firstKey = cursorKey(cursor);
  if (firstKey) seen.add(firstKey);

  const located = findById(items, targetId);
  if (located) {
    return { status: 'TARGET_FOUND', items, cursor, hasMore, target: located };
  }
  if (!hasMore) {
    return { status: 'NOT_FOUND', items, cursor, hasMore: false, target: null };
  }

  let pages = 1;
  while (pages < maxPages) {
    const requestCursor = normalizedPageCursor(cursor);
    if (!requestCursor) {
      return { status: 'CURSOR_INVALID_OR_REPEATED', items, cursor, hasMore, target: null };
    }
    let page;
    try {
      page = await loadPage(requestCursor);
    } catch (error) {
      return { status: 'REQUEST_FAILED', items, cursor, hasMore, target: null, error };
    }
    pages += 1;
    const nextKey = cursorKey(page?.cursor);
    items = appendUniqueComments(items, page?.items);
    cursor = normalizedPageCursor(page?.cursor);
    hasMore = Boolean(page?.hasMore) && cursor != null;
    const hit = findById(items, targetId);
    if (hit) {
      return { status: 'TARGET_FOUND', items, cursor, hasMore, target: hit };
    }
    if (!hasMore) {
      return { status: 'NOT_FOUND', items, cursor, hasMore: false, target: null };
    }
    if (!nextKey || seen.has(nextKey)) {
      return { status: 'CURSOR_INVALID_OR_REPEATED', items, cursor, hasMore, target: null };
    }
    seen.add(nextKey);
  }
  return { status: 'BOUNDED_PAGE_LIMIT_REACHED', items, cursor, hasMore, target: null };
}
